import React, { Fragment, useEffect, useState } from 'react';
import type { ChangeEvent } from 'react';
import { Card, Form, Table } from 'react-bootstrap-5';

import withDragDropContext from 'helpers/react_dnd/with_drag_drop_context';

import RentRollEntry from './rent_roll_entry';
import type {
  CustomFieldSchema, RentRollCustomFieldValue, RentRollEntryData, ScalarEntryField
} from './rent_roll_entry';
import { useBuildingSize } from '../property_edit_form/hooks/use_building_size';
import { useCurrencyLabel } from '../property_edit_form/hooks/use_currency_label';
import { useIncludeRentRoll } from '../property_edit_form/hooks/use_include_rent_roll';
import { useMeasurementAreaLabel } from '../property_edit_form/hooks/use_measurement_area_label';
import { usePropertyTypeOptions } from '../property_edit_form/hooks/use_property_type_options';

export type { RentRollEntryData };

export type RentRollShowSettings = {
  suite: boolean;
  bedrooms: boolean;
  bathrooms: boolean;
  tenantName: boolean;
  sizeSf: boolean;
  percentOfBuilding: boolean;
  pricePerSfPerYear: boolean;
  rent: boolean;
  marketRent: boolean;
  rentPerSf: boolean;
  annualRent: boolean;
  securityDeposit: boolean;
  leaseStart: boolean;
  leaseEnd: boolean;
  useMonthlyInput: boolean;
};

export type RentRollTableProps = {
  buildingSizeSf: number | null;
  customFieldSchemas: CustomFieldSchema[];
  entries: RentRollEntryData[];
  rentRoll: RentRollShowSettings;
  showSyndicateRentRoll: boolean;
  syndicateRentRoll: boolean;
};

const HOSPITALITY_PROPERTY_TYPE_ID = 8;
const MULTI_FAMILY_PROPERTY_TYPE_ID = 6;

const NON_MULTI_FAMILY_COLUMNS: ReadonlyArray<keyof RentRollShowSettings> = [
  'percentOfBuilding',
  'tenantName',
  'pricePerSfPerYear',
  'annualRent',
];

const MULTI_FAMILY_COLUMNS: ReadonlyArray<keyof RentRollShowSettings> = [
  'bedrooms',
  'bathrooms',
  'rent',
  'rentPerSf',
  'securityDeposit',
];

const RENT_ROLL_ROOT = 'property[rent_roll_attributes]';
const ENTRIES_ROOT = `${RENT_ROLL_ROOT}[entries_attributes]`;

const entryFieldName = (index: number, field: string) => `${ENTRIES_ROOT}[${index}][${field}]`;
const showFieldName = (field: string) => `${RENT_ROLL_ROOT}[show_${field}]`;

const hiddenValue = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined) return '';
  return String(value);
};

const sumField = (entries: RentRollEntryData[], field: ScalarEntryField): number => {
  return entries.reduce((acc, entry) => {
    const num = Number(entry[field]);
    return Number.isFinite(num) ? acc + num : acc;
  }, 0);
};

const formatTotalSize = (value: number): string => (value ? value.toLocaleString() : '');
const formatTotalMoney = (value: number, currencyLabel: string): string => {
  if (value === 0) return '';
  const currency = currencyLabel === '€' ? 'EUR' : currencyLabel;
  try {
    return value.toLocaleString(undefined, { maximumFractionDigits: 2, style: 'currency', currency });
  } catch {
    return `${currencyLabel}${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  }
};

const blankEntry = (sortOrder: number, customFieldSchemas: CustomFieldSchema[]): RentRollEntryData => ({
  id: null,
  sortOrder,
  suite: null,
  propertyTypeId: null,
  propertyTypeLabel: null,
  bedrooms: null,
  bathrooms: null,
  tenantName: null,
  size: null,
  pricePerSfPerYear: null,
  rent: null,
  marketRent: null,
  annualRent: null,
  securityDeposit: null,
  leaseStart: null,
  leaseEnd: null,
  comments: null,
  recoveryType: null,
  escalations: [],
  customFieldValues: customFieldSchemas.map(schema => ({
    id: null,
    customFieldSchemaId: schema.id,
    value: schema.defaultValue,
  })),
});

const HIDDEN_FIELDS: Array<[string, ScalarEntryField]> = [
  ['id', 'id'],
  ['sort_order', 'sortOrder'],
  ['suite', 'suite'],
  ['property_type_id', 'propertyTypeId'],
  ['bedrooms_casted', 'bedrooms'],
  ['bathrooms_casted', 'bathrooms'],
  ['tenant_name', 'tenantName'],
  ['size', 'size'],
  ['price_per_unit_per_year', 'pricePerSfPerYear'],
  ['rent', 'rent'],
  ['market_rent', 'marketRent'],
  ['annual_rent', 'annualRent'],
  ['security_deposit', 'securityDeposit'],
  ['lease_start', 'leaseStart'],
  ['lease_end', 'leaseEnd'],
  ['comments', 'comments'],
  ['recovery_type', 'recoveryType'],
];

type ShowColumn = {
  showKey: keyof RentRollShowSettings;
  railsField: string;
  label: React.ReactNode;
};

const RentRollTable = ({
  buildingSizeSf,
  customFieldSchemas,
  entries: initialEntries,
  rentRoll: initialRentRoll,
  showSyndicateRentRoll,
  syndicateRentRoll: initialSyndicateRentRoll
}: RentRollTableProps) => {
  const [entries, setEntries] = useState<RentRollEntryData[]>(initialEntries);
  const [rentRoll, setRentRoll] = useState<RentRollShowSettings>(initialRentRoll);
  const [syndicateRentRoll, setSyndicateRentRoll] = useState<boolean>(!!initialSyndicateRentRoll);
  const buildingSize = useBuildingSize(buildingSizeSf);
  const propertyTypeOptions = usePropertyTypeOptions();
  const includeRentRoll = useIncludeRentRoll();
  const currencyLabel = useCurrencyLabel();
  const measurementAreaLabel = useMeasurementAreaLabel();
  const multiType = propertyTypeOptions.length > 1;
  const isHospitality = propertyTypeOptions[0]?.id === HOSPITALITY_PROPERTY_TYPE_ID;
  const isOnlyMultiFamily = propertyTypeOptions.length === 1
    && propertyTypeOptions[0]?.id === MULTI_FAMILY_PROPERTY_TYPE_ID;
  const hasMultiFamily = propertyTypeOptions.some(
    option => option.id === MULTI_FAMILY_PROPERTY_TYPE_ID,
  );
  const hideTable = !includeRentRoll || isHospitality;
  const hiddenColumns = new Set<string>([
    ...(isOnlyMultiFamily ? NON_MULTI_FAMILY_COLUMNS : []),
    ...(hasMultiFamily ? [] : MULTI_FAMILY_COLUMNS),
  ]);

  useEffect(() => {
    const validIds = new Set(propertyTypeOptions.map(option => option.id));
    setEntries(prev => prev.map(entry => (
      entry.propertyTypeId != null && !validIds.has(entry.propertyTypeId)
        ? { ...entry, propertyTypeId: null }
        : entry
    )));
  }, [propertyTypeOptions]);

  const updateEntry = (index: number) => (next: RentRollEntryData) => {
    setEntries(prev => prev.map((entry, i) => (i === index ? next : entry)));
  };

  const reorderEntries = (dragIndex: number, hoverIndex: number) => {
    setEntries((prev) => {
      const next = [...prev];
      const [moved] = next.splice(dragIndex, 1);
      next.splice(hoverIndex, 0, moved);
      return next.map((entry, i) => ({ ...entry, sortOrder: i }));
    });
  };

  const duplicateEntry = (index: number) => () => {
    setEntries((prev) => {
      const source = prev[index];
      const copy: RentRollEntryData = {
        ...source,
        id: null,
        markedForDeletion: false,
        escalations: source.escalations.map(esc => ({ ...esc })),
        customFieldValues: source.customFieldValues.map(value => ({ ...value, id: null })),
      };
      const next = [...prev];
      next.splice(index + 1, 0, copy);
      return next.map((entry, i) => ({ ...entry, sortOrder: i }));
    });
  };

  const deleteEntry = (index: number) => () => {
    setEntries(prev => prev.map((entry, i) => (
      i === index ? { ...entry, markedForDeletion: true } : entry
    )));
  };

  const addRows = (count: number) => () => {
    setEntries((prev) => {
      const next = [...prev];
      for (let i = 0; i < count; i += 1) {
        next.push(blankEntry(next.length, customFieldSchemas));
      }
      return next;
    });
  };

  const visibleEntries = entries.filter(entry => !entry.markedForDeletion);

  const toggleShow = (key: keyof RentRollShowSettings) => (event: ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setRentRoll(prev => ({ ...prev, [key]: checked }));
  };

  const allColumns: ShowColumn[] = [
    { showKey: 'suite', railsField: 'suite', label: isOnlyMultiFamily ? 'Unit' : 'Suite' },
    { showKey: 'bedrooms', railsField: 'bedrooms', label: 'Beds' },
    { showKey: 'bathrooms', railsField: 'bathrooms', label: 'Baths' },
    { showKey: 'tenantName', railsField: 'tenant_name', label: 'Tenant' },
    { showKey: 'sizeSf', railsField: 'size_sf', label: `Size (${measurementAreaLabel})` },
    { showKey: 'percentOfBuilding', railsField: 'percent_of_building', label: '% of Bldg' },
    {
      showKey: 'pricePerSfPerYear',
      railsField: 'price_per_sf_per_year',
      label: `${currencyLabel}/${measurementAreaLabel}/${rentRoll.useMonthlyInput ? 'm' : 'yr'}`,
    },
    { showKey: 'rent', railsField: 'rent', label: 'Rent' },
    { showKey: 'marketRent', railsField: 'market_rent', label: 'Mkt Rent' },
    { showKey: 'rentPerSf', railsField: 'rent_per_sf', label: 'Rent/SF' },
    {
      showKey: 'annualRent',
      railsField: 'annual_rent',
      label: rentRoll.useMonthlyInput ? 'Monthly Rent' : 'Annual Rent',
    },
    { showKey: 'securityDeposit', railsField: 'security_deposit', label: 'Security Deposit' },
    { showKey: 'leaseStart', railsField: 'lease_start', label: 'Lease Start' },
    { showKey: 'leaseEnd', railsField: 'lease_end', label: 'Lease End' },
  ];
  const columns = allColumns.filter(column => !hiddenColumns.has(column.showKey));

  return (
    <Card
      className="mb-4"
      data-testid="react-rent-roll-table"
      style={hideTable ? { display: 'none' } : undefined}
    >
      <Card.Header className="bg-light d-flex align-items-center justify-content-between">
        <strong>Rent Roll</strong>
        <label className="d-flex align-items-center gap-2 mb-0">
          <span>Show:</span>
          <Form.Select
            value={rentRoll.useMonthlyInput ? 'monthly' : 'annual'}
            onChange={e => setRentRoll(prev => ({ ...prev, useMonthlyInput: e.target.value === 'monthly' }))}
          >
            <option value="annual">Annual Rent</option>
            <option value="monthly">Monthly Rent</option>
          </Form.Select>
        </label>
      </Card.Header>
      {showSyndicateRentRoll && (
        <Card.Body className="border-bottom">
          <Form.Check
            checked={syndicateRentRoll}
            id="property_syndicate_rent_roll"
            label="Syndicate Rent Roll"
            type="checkbox"
            onChange={event => setSyndicateRentRoll(event.target.checked)}
          />
        </Card.Body>
      )}
      <Card.Body className="p-0">
        <div className="table-container border-0 rounded-0">
          <Table bordered className="align-middle mb-0" size="sm">
            <thead>
              <tr>
                <th key="drag-handle-checkbox" />
                <th key="escalations-toggle-checkbox" />
                {columns.map((column, index) => {
                  const cells = [
                    <th key={column.showKey}>
                      <Form.Check
                        checked={rentRoll[column.showKey]}
                        type="checkbox"
                        onChange={toggleShow(column.showKey)}
                      />
                    </th>,
                  ];
                  if (multiType && index === 1) {
                    cells.unshift(<th key="multi-type-checkbox" />);
                  }
                  return cells;
                })}
                {customFieldSchemas.map(schema => (
                  <th key={`cf-checkbox-${schema.id}`} />
                ))}
                <th key="actions-checkbox" />
              </tr>
              <tr>
                <th key="drag-handle-label" />
                <th key="escalations-toggle-label" />
                {columns.map((column, index) => {
                  const cells = [<th className="text-nowrap" key={column.showKey}>{column.label}</th>];
                  if (multiType && index === 1) {
                    cells.unshift(<th key="multi-type-label">Type</th>);
                  }
                  return cells;
                })}
                {customFieldSchemas.map(schema => (
                  <th className="text-nowrap" key={`cf-label-${schema.id}`}>{schema.label}</th>
                ))}
                <th key="actions-label" />
              </tr>
            </thead>
            <tbody>
              {visibleEntries.length === 0 && (
                <tr>
                  <td
                    className="text-center text-muted"
                    colSpan={
                      (multiType ? columns.length + 1 : columns.length) + customFieldSchemas.length + 3
                    }
                  >
                    No rent roll entries
                  </td>
                </tr>
              )}
              {entries.map((entry, index) => {
                if (entry.markedForDeletion) return null;
                return (
                  <RentRollEntry
                    buildingSize={buildingSize}
                    currencyLabel={currencyLabel}
                    customFieldSchemas={customFieldSchemas}
                    entry={entry}
                    hiddenColumns={hiddenColumns}
                    index={index}
                    key={entry.id ?? `new-${index}`}
                    measurementAreaLabel={measurementAreaLabel}
                    propertyTypeOptions={propertyTypeOptions}
                    useMonthly={rentRoll.useMonthlyInput}
                    onChange={updateEntry(index)}
                    onDelete={deleteEntry(index)}
                    onDuplicate={duplicateEntry(index)}
                    onReorder={reorderEntries}
                  />
                );
              })}
            </tbody>
            {visibleEntries.length > 0 && (
              <tfoot>
                <tr className="fw-semibold">
                  <td colSpan={2}>Totals</td>
                  {columns.map((column, index) => {
                    let content: React.ReactNode = '';
                    if (column.showKey === 'sizeSf') {
                      content = formatTotalSize(sumField(visibleEntries, 'size'));
                    } else if (column.showKey === 'rent') {
                      content = formatTotalMoney(sumField(visibleEntries, 'rent'), currencyLabel);
                    } else if (column.showKey === 'marketRent') {
                      content = formatTotalMoney(sumField(visibleEntries, 'marketRent'), currencyLabel);
                    } else if (column.showKey === 'annualRent') {
                      const totalAnnual = sumField(visibleEntries, 'annualRent');
                      content =
                        formatTotalMoney(rentRoll.useMonthlyInput ? totalAnnual / 12 : totalAnnual, currencyLabel);
                    } else if (column.showKey === 'securityDeposit') {
                      content = formatTotalMoney(sumField(visibleEntries, 'securityDeposit'), currencyLabel);
                    }
                    const cells = [<td key={column.showKey}>{content}</td>];
                    if (multiType && index === 1) {
                      cells.unshift(<td key="multi-type-total" />);
                    }
                    return cells;
                  })}
                  {customFieldSchemas.map(schema => (
                    <td key={`cf-total-${schema.id}`} />
                  ))}
                  <td />
                </tr>
              </tfoot>
            )}
          </Table>
          <div className="d-flex gap-4 p-3">
            <a className="clickable text-primary" onClick={addRows(1)}>+ add a row</a>
            <a className="clickable text-primary" onClick={addRows(5)}>+ add 5 rows</a>
          </div>
        </div>
      </Card.Body>
      <input name={`${RENT_ROLL_ROOT}[use_monthly_input]`} type="hidden" value="0" />
      <input
        name={`${RENT_ROLL_ROOT}[use_monthly_input]`}
        type="hidden"
        value={rentRoll.useMonthlyInput ? '1' : '0'}
      />
      <input name="property[syndicate_rent_roll]" type="hidden" value="0" />
      <input
        name="property[syndicate_rent_roll]"
        type="hidden"
        value={syndicateRentRoll ? '1' : '0'}
      />
      {columns.map(column => (
        <Fragment key={`show-${column.railsField}`}>
          <input name={showFieldName(column.railsField)} type="hidden" value="0" />
          <input
            name={showFieldName(column.railsField)}
            type="hidden"
            value={rentRoll[column.showKey] ? '1' : '0'}
          />
        </Fragment>
      ))}
      {entries.map((entry, index) => (
        <Fragment key={`hidden-${entry.id ?? `new-${index}`}`}>
          {HIDDEN_FIELDS.map(([railsName, dataField]) => (
            <input
              key={railsName}
              name={entryFieldName(index, railsName)}
              type="hidden"
              value={hiddenValue(entry[dataField])}
            />
          ))}
          <input
            name={entryFieldName(index, '_destroy')}
            type="hidden"
            value={entry.markedForDeletion ? '1' : '0'}
          />
          {entry.escalations.map((esc, escIndex) => (
            // eslint-disable-next-line react/no-array-index-key
            <Fragment key={`escalation-${escIndex}`}>
              <input
                name={`${ENTRIES_ROOT}[${index}][escalations][${escIndex}][date]`}
                type="hidden"
                value={esc.date ?? ''}
              />
              <input
                name={`${ENTRIES_ROOT}[${index}][escalations][${escIndex}][price_per_sf_per_year]`}
                type="hidden"
                value={hiddenValue(esc.pricePerSfPerYear)}
              />
            </Fragment>
          ))}
          {entry.customFieldValues.map((cfv: RentRollCustomFieldValue, cfvIndex) => (
            <Fragment key={`cfv-${cfv.customFieldSchemaId}`}>
              <input
                name={`${ENTRIES_ROOT}[${index}][custom_field_values_attributes][${cfvIndex}][custom_field_schema_id]`}
                type="hidden"
                value={String(cfv.customFieldSchemaId)}
              />
              {cfv.id != null && (
                <input
                  name={`${ENTRIES_ROOT}[${index}][custom_field_values_attributes][${cfvIndex}][id]`}
                  type="hidden"
                  value={String(cfv.id)}
                />
              )}
              <input
                name={`${ENTRIES_ROOT}[${index}][custom_field_values_attributes][${cfvIndex}][value]`}
                type="hidden"
                value={cfv.value ?? ''}
              />
            </Fragment>
          ))}
        </Fragment>
      ))}
    </Card>
  );
};

export default withDragDropContext(RentRollTable);
